<template>
	<Headroom :offset="20" @pin="onHeaderPin" @unpin="onHeaderUnpin">
		<header class="main-header relative z-50 flex h-20 flex-wrap">
			<div
				class="main-header--nav relative z-20 flex w-full items-center justify-between px-5 duration-200"
			>
				<div class="flex w-60 items-center">
					<CarbarLink href="/">
						<IconCarbarLogo />
					</CarbarLink>
				</div>

				<div class="flex gap-2">
					<CarbarButton
						v-if="isCancellable"
						:is-outline="true"
						:size="4"
						data-type="cancel-order"
						@click="isCancelModalOpen = true"
					>
						Cancel
					</CarbarButton>
					<CarbarLink v-if="showSaveButton">
						<CarbarButton
							:size="4"
							background-color="bg-blue-400 hover:bg-sky-600"
						>
							Save & check-out later
						</CarbarButton>
					</CarbarLink>
				</div>
			</div>
		</header>
	</Headroom>

	<div class="h-20" />

	<BaseModal
		:show="isCancelModalOpen"
		:show-title="true"
		padding="py-16 px-8"
		@close="closeCancelModal"
	>
		<template #title>
			<h3 class="text-center text-xl font-normal text-gray-700">
				Order cancellation
			</h3>
		</template>
		<template #content>
			<p class="mt-2 text-center text-lg">
				You are about to cancel your current order progress. Do you
				really want to proceed with cancellation?
			</p>
			<p v-if="cancelError" class="mt-4 text-center text-sm text-red-400">
				{{ cancelError }}
			</p>
			<div class="mt-6 flex flex-col items-center gap-6">
				<CarbarButton
					width="w-fit"
					:disabled="isCancellingOrder"
					:show-loader="isCancellingOrder"
					@click="cancelOrder"
				>
					Yes, cancel order
				</CarbarButton>
				<CarbarLink
					width="w-fit"
					text="No, continue my order"
					class="cursor-pointer"
					@click="closeCancelModal"
				/>
			</div>
		</template>
	</BaseModal>
</template>

<script setup lang="ts">
import IconCarbarLogo from '@/assets/svg/logo-carbar-v3.svg'
import CarbarButton from '@/components/buttons/CarbarButton.vue'
import CarbarLink from '@/components/links/CarbarLink.vue'
import BaseModal from '@/components/modals/BaseModal.vue'
import { useOrderCheckout } from '@/stores/order-checkout'
import Headroom from '@/vendor/headroom/headroom.vue'

interface Props {
	showSaveButton?: boolean
}

withDefaults(defineProps<Props>(), {
	showSaveButton: false,
})

const orderCheckout = useOrderCheckout()
const route = useRoute()

const isCancelModalOpen = ref(false)
const isCancellingOrder = ref(false)
const cancelError = ref('')

// Hidden once the order is paid
const isCancellable = computed(
	() =>
		Boolean(orderCheckout.checkout) &&
		getCheckoutStepFromPath(route.path) !== 'thank-you'
)

const closeCancelModal = () => {
	isCancelModalOpen.value = false
	cancelError.value = ''
}

const cancelOrder = async () => {
	isCancellingOrder.value = true
	cancelError.value = ''

	try {
		await orderCheckout.cancelOrder()
	} catch (error) {
		cancelError.value = (error as Error).message
	} finally {
		isCancellingOrder.value = false
	}
}

const onHeaderPin = () => {
	document.body.classList.add(...['header-pinned'])
}

const onHeaderUnpin = () => {
	document.body.classList.remove(...['header-pinned'])
}
</script>
